/**
 * Compression utilities for large route data
 * Uses LZ-string for browser-compatible compression
 */

// We'll use a lightweight compression library
// First install: npm install lz-string

import LZString from 'lz-string';

/**
 * Compress data to reduce size for Firestore storage
 */
export function compressData(data: any): string {
  const jsonString = JSON.stringify(data);
  const compressed = LZString.compressToBase64(jsonString);
  
  const originalSize = jsonString.length;
  const compressedSize = compressed.length;
  const ratio = ((1 - compressedSize / originalSize) * 100).toFixed(1);
  
  console.log(`🗜️ Compressed: ${(originalSize / 1024).toFixed(1)}KB → ${(compressedSize / 1024).toFixed(1)}KB (${ratio}% reduction)`);
  
  return compressed;
}

/**
 * Decompress data back to original format
 */
export function decompressData<T>(compressed: string): T {
  const jsonString = LZString.decompressFromBase64(compressed);
  return JSON.parse(jsonString);
}

/**
 * Check if data should be compressed
 */
export function shouldCompress(data: any, thresholdKB: number = 100): boolean {
  const size = JSON.stringify(data).length / 1024;
  return size > thresholdKB;
}

/**
 * Compress specific fields of a route
 */
export function compressRouteFields(route: any): any {
  const compressed = { ...route };

  // Compress large fields individually
  if (route.coordinates && shouldCompress(route.coordinates, 50)) {
    compressed.coordinates = compressData(route.coordinates);
    compressed._compressed = { ...compressed._compressed, coordinates: true };
  }

  if (route.unpavedSegments && shouldCompress(route.unpavedSegments, 50)) {
    compressed.unpavedSegments = compressData(route.unpavedSegments);
    compressed._compressed = { ...compressed._compressed, unpavedSegments: true };
  }

  if (route.highwaySegments && shouldCompress(route.highwaySegments, 50)) {
    compressed.highwaySegments = compressData(route.highwaySegments);
    compressed._compressed = { ...compressed._compressed, highwaySegments: true };
  }

  if (route.surfaces && shouldCompress(route.surfaces, 50)) {
    compressed.surfaces = compressData(route.surfaces);
    compressed._compressed = { ...compressed._compressed, surfaces: true };
  }

  // Compress highways if they exist
  if (route.highways && shouldCompress(route.highways, 50)) {
    compressed.highways = compressData(route.highways);
    compressed._compressed = { ...compressed._compressed, highways: true };
  }

  if (route.elevation?.profile && shouldCompress(route.elevation.profile, 50)) {
    compressed.elevation = {
      ...route.elevation,
      profile: compressData(route.elevation.profile)
    };
    compressed._compressed = { ...compressed._compressed, elevationProfile: true };
  }

  return compressed;
}

/**
 * Decompress route fields back to original
 */
export function decompressRouteFields(route: any): any {
  if (!route._compressed) return route;

  const decompressed = { ...route };

  if (route._compressed.coordinates && typeof route.coordinates === 'string') {
    decompressed.coordinates = decompressData(route.coordinates);
  }

  if (route._compressed.unpavedSegments && typeof route.unpavedSegments === 'string') {
    decompressed.unpavedSegments = decompressData(route.unpavedSegments);
  }

  if (route._compressed.highwaySegments && typeof route.highwaySegments === 'string') {
    decompressed.highwaySegments = decompressData(route.highwaySegments);
  }

  if (route._compressed.surfaces && typeof route.surfaces === 'string') {
    decompressed.surfaces = decompressData(route.surfaces);
  }

  // Decompress highways if they exist
  if (route._compressed.highways && typeof route.highways === 'string') {
    decompressed.highways = decompressData(route.highways);
  }

  if (route._compressed.elevationProfile && route.elevation?.profile && typeof route.elevation.profile === 'string') {
    decompressed.elevation = {
      ...route.elevation,
      profile: decompressData(route.elevation.profile)
    };
  }

  // Remove compression metadata
  delete decompressed._compressed;

  return decompressed;
}
